<template>
	<div
		ref="listRef"
		class="relative flex items-center w-full md:w-[calc(20%-20px)] min-h-[56px] pl-0 sm:pl-4 md:pl-6 pr-0 sm:pr-4 pb-2 md:pb-0 md:mt-0 mt-3"
	>
		<div @click="showList = !showList" class="flex flex-col flex-1 w-[calc(100%-30px)] cursor-pointer">
			<span class="pb-1 text-dj-gray-dark text-sm font-medium">{{ $t("companies.searchbar.extra.title") }}</span>
			<span
				v-if="selectedExtras.length || selectedSzmds.length"
				class="block w-[calc(100%-6px)] whitespace-nowrap overflow-ellipsis overflow-hidden"
			>
				{{ selectedExtras.map((extra) => extra.name[locale]).join(", ") }}
				<template v-if="selectedExtras.length > 0">,</template>
				{{ selectedSzmds.map((szmd) => szmd.name[locale]).join(", ") }}
			</span>
			<span v-else class="text-dj-gray">{{ placeholder }}</span>
		</div>

		<DropdownButton v-model="showList" class="mt-2" />

		<div
			v-if="showList"
			class="absolute p-5 left-0 top-[60px] md:top-[85px] w-[300px] max-w-[100%] md:max-w-[100vw] h-[max] bg-white border border-dj-gray-light rounded-big flex flex-col gap-y-[10px] z-30"
		>
			<BaseCheckbox
				v-for="(szmd, index) in szmds.slice().reverse()"
				:key="index"
				v-model="szmd.value"
				@change="onChangeSzmd(szmd)"
			>
				{{ szmd.name[locale] }}
			</BaseCheckbox>
			<hr />
			<BaseCheckbox
				v-for="(extra, index) in extras"
				:key="index"
				v-model="extra.value"
				@change="onChangeExtra(extra)"
			>
				{{ extra.name[locale] }}
			</BaseCheckbox>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { onClickOutside } from "@vueuse/core";
import { useI18n } from "vue-i18n";

export default defineComponent({
	name: "SearchbarCompaniesExtra",
	props: {
		modelValue: {
			type: Object,
			required: true,
		},
		filterList: {
			type: Object,
			default: {},
		},
		placeholder: {
			type: String,
			default: "",
		},
	},
	emits: ["update:modelValue"],
	async setup(props, { emit }) {
		const { locale } = useI18n();

		onMounted(() => {
			addValuePropToExtras();
			getValuesFromUrl();
		});

		const selectedExtras = ref([]);
		const extrasData = ref([]);
		const extras = ref([]);

		const selectedSzmds = ref([]);
		const szmdsData = ref([]);
		const szmds = ref([]);

		const showList = ref(false);
		const listRef = ref(null);

		extrasData.value = props.filterList.badge;
		szmdsData.value = props.filterList.szmd;

		function addValuePropToExtras() {
			extras.value = sortArrayByObjectByLocale(extrasData.value, "name", locale.value).value.map((extra) => {
				return {
					...extra,
					value: false,
				};
			});

			for (const [year, type] of Object.entries(szmdsData.value)) {
				type.forEach((el) => {
					szmds.value.push({
						name: {
							en: `${year} ${langs[el]["en"]}`,
							hu: `${year} ${langs[el]["hu"]}`,
							ro: `${year} ${langs[el]["ro"]}`,
						},
						id: `${year}${el}`,
						value: false,
					});
				});
			}
		}

		function onChangeExtra(extra) {
			const index = extras.value.findIndex((pred) => pred.name[locale.value] == extra.name[locale.value]);
			if (extras.value[index].value) {
				selectedExtras.value.push(extra);
				emitGroupExtras();
			} else {
				selectedExtras.value = selectedExtras.value.filter(
					(sExtra) => sExtra.name[locale.value] != extra.name[locale.value]
				);
				emitGroupExtras();
			}
		}

		function onChangeSzmd(szmd) {
			const index = szmds.value.findIndex((pred) => pred.id == szmd.id);
			if (szmds.value[index].value) {
				selectedSzmds.value.push(szmd);
				emitGroupExtras();
			} else {
				selectedSzmds.value = selectedSzmds.value.filter((sSzmd) => sSzmd.id != szmd.id);
				emitGroupExtras();
			}
		}

		function getValuesFromUrl() {
			const query = useRoute().query;

			if (query.extra) {
				const extrasFromUrl = query.extra
					.toString()
					.split(",")
					.map((t) => Number.parseInt(t));

				extras.value.forEach((extra, index) => {
					extrasFromUrl.forEach((extraFU) => {
						if (extra.id == extraFU) {
							extras.value[index].value = true;
							selectedExtras.value.push(extras.value[index]);
						}
					});
				});

				emitGroupExtras();
			}

			if (query.szmd) {
				const szmdsFromUrl = query.szmd
					.toString()
					.split(",")
					.map((t) => t);

				szmds.value.forEach((szmd, index) => {
					szmdsFromUrl.forEach((szmdFU) => {
						if (szmd.id == szmdFU) {
							szmds.value[index].value = true;
							selectedSzmds.value.push(szmds.value[index]);
						}
					});
				});

				emitGroupExtras();
			}
		}

		function emitGroupExtras() {
			emit("update:modelValue", { extra: selectedExtras.value, szmd: selectedSzmds.value });
		}

		onClickOutside(listRef, () => {
			showList.value = false;
		});

		const langs = {
			awarded: {
				en: "Lovable Workplaces Awarded",
				hu: "Szerethető Munkahelyek Díjazott",
				ro: "Lovable Workplaces Awarded",
			},
			participant: {
				en: "Lovable Workplaces Participant",
				hu: "Szerethető Munkahelyek Nevezett",
				ro: "Lovable Workplaces Participant",
			},
		};

		return {
			langs,
			extras,
			szmds,
			selectedExtras,
			selectedSzmds,
			showList,
			listRef,
			locale,
			onChangeExtra,
			onChangeSzmd,
		};
	},
});
</script>
